import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { PROVINCES, TRADE_CATEGORIES } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { X, Building2 } from "lucide-react";

export default function ProfileEditPage() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    business_name: "", bio: "", province: "", city: "",
    categories: [], years_experience: 0, certifications: [],
    email: "", phone: "",
    company_name: "", business_number: "",
    price_per_hour: 85, completion_rate: 95, avg_response_time_hours: 4,
  });
  const [certInput, setCertInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) nav("/login");
    if (user?.profile) {
      const p = user.profile;
      setForm({
        business_name: p.business_name || "",
        bio: p.bio || "",
        province: p.province || "",
        city: p.city || "",
        categories: p.categories || [],
        years_experience: p.years_experience || 0,
        certifications: p.certifications || [],
        email: p.email || "",
        phone: p.phone || "",
        company_name: p.company_name || "",
        business_number: p.business_number || "",
        price_per_hour: p.price_per_hour ?? 85,
        completion_rate: p.completion_rate ?? 95,
        avg_response_time_hours: p.avg_response_time_hours ?? 4,
      });
    }
  }, [user, loading, nav]);

  if (!user) return null;

  if (user.role !== "tradesperson") {
    // Homeowners can still access this page — only to switch to a Contractor account.
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="font-display font-bold text-3xl text-slate-900 mb-2">Account settings</h1>
        <p className="text-slate-600 mb-8">You&apos;re signed in as a Homeowner. Switch to a Contractor account to receive job leads and build a business profile.</p>
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <RoleSwitcher currentRole={user.role} onSwitched={refresh} />
          </CardContent>
        </Card>
      </div>
    );
  }

  const toggleCategory = (c) => {
    const has = form.categories.includes(c);
    setForm({ ...form, categories: has ? form.categories.filter((x) => x !== c) : [...form.categories, c] });
  };

  const addCert = () => {
    if (certInput.trim()) {
      setForm({ ...form, certifications: [...form.certifications, certInput.trim()] });
      setCertInput("");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/me/profile", {
        ...form,
        years_experience: parseInt(form.years_experience, 10) || 0,
        price_per_hour: parseFloat(form.price_per_hour) || 0,
        completion_rate: parseFloat(form.completion_rate) || 0,
        avg_response_time_hours: parseFloat(form.avg_response_time_hours) || 0,
      });
      await refresh();
      toast.success("Profile saved");
      nav("/dashboard");
    } catch (err) {
      const d = err.response?.data?.detail;
      let msg = "Failed to save";
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d) && d[0]?.msg) msg = d[0].msg.replace(/^Value error,\s*/, "");
      toast.error(msg);
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="font-display font-bold text-3xl text-slate-900 mb-2">Your trade profile</h1>
      <p className="text-slate-600 mb-8">Homeowners see this when reviewing your quotes.</p>
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <form onSubmit={save} className="space-y-5">
            <div>
              <Label>Business name (optional)</Label>
              <Input data-testid="profile-business" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label>Bio</Label>
              <Textarea data-testid="profile-bio" rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Describe your trade, experience, and service area…" className="mt-1.5" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Province</Label>
                <Select value={form.province} onValueChange={(v) => setForm({ ...form, province: v })}>
                  <SelectTrigger data-testid="profile-province" className="mt-1.5"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{PROVINCES.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Primary city</Label>
                <Input data-testid="profile-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>Years of experience</Label>
              <Input data-testid="profile-years" type="number" value={form.years_experience} onChange={(e) => setForm({ ...form, years_experience: e.target.value })} className="mt-1.5 max-w-[160px]" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Contact email</Label>
                <Input
                  data-testid="profile-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="contact@yourbusiness.ca"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  data-testid="profile-phone-input"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+1 (416) 555-0123"
                  className="mt-1.5"
                />
              </div>
            </div>

            <Separator />
            <div className="flex items-center gap-2 text-slate-700">
              <Building2 className="w-4 h-4" />
              <span className="font-medium">Canadian business info</span>
              <span className="text-xs text-slate-500">(optional — shown as a trust signal to homeowners)</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Registered company name</Label>
                <Input
                  data-testid="profile-company-name"
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  placeholder="e.g. Howard Plumbing Ltd."
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>CRA Business Number</Label>
                <Input
                  data-testid="profile-business-number"
                  value={form.business_number}
                  onChange={(e) => setForm({ ...form, business_number: e.target.value })}
                  placeholder="123456789 or 123456789RT0001"
                  className="mt-1.5"
                />
                <p className="text-xs text-slate-500 mt-1">9-digit BN. Adding this earns a Verified badge.</p>
              </div>
            </div>
            <Separator />
            <div className="flex items-center gap-2 text-slate-700">
              <span className="font-medium">Pricing & availability</span>
              <span className="text-xs text-slate-500">(used to match you with the right jobs)</span>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>Hourly rate (CAD)</Label>
                <Input
                  data-testid="profile-price-hour"
                  type="number"
                  min="0"
                  value={form.price_per_hour}
                  onChange={(e) => setForm({ ...form, price_per_hour: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Job completion rate (%)</Label>
                <Input
                  data-testid="profile-completion-rate"
                  type="number"
                  min="0"
                  max="100"
                  value={form.completion_rate}
                  onChange={(e) => setForm({ ...form, completion_rate: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Avg response time (hours)</Label>
                <Input
                  data-testid="profile-response-hours"
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.avg_response_time_hours}
                  onChange={(e) => setForm({ ...form, avg_response_time_hours: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>

            <div>
              <Label>Trade categories</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {TRADE_CATEGORIES.map((c) => {
                  const active = form.categories.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      data-testid={`profile-cat-${c}`}
                      onClick={() => toggleCategory(c)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${active ? "bg-blue-900 text-white border-blue-900" : "border-slate-300 text-slate-700 hover:border-slate-400"}`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Certifications & licenses</Label>
              <div className="flex gap-2 mt-1.5">
                <Input data-testid="profile-cert-input" value={certInput} onChange={(e) => setCertInput(e.target.value)} placeholder="e.g. Red Seal Plumber" />
                <Button type="button" data-testid="profile-cert-add" onClick={addCert} variant="outline">Add</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {form.certifications.map((c) => (
                  <Badge key={`cert-${c}`} variant="outline" className="border-blue-200 text-blue-900 font-normal gap-1.5">
                    {c}
                    <button type="button" onClick={() => setForm({ ...form, certifications: form.certifications.filter((x) => x !== c) })}>
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
            <Button type="submit" data-testid="profile-save" disabled={saving} className="w-full bg-orange-600 hover:bg-orange-700 text-white h-11">
              {saving ? "Saving…" : "Save Profile"}
            </Button>
          </form>

          <Separator className="my-6" />
          <RoleSwitcher currentRole={user.role} onSwitched={refresh} />
        </CardContent>
      </Card>
    </div>
  );
}

function RoleSwitcher({ currentRole, onSwitched }) {
  const nav = useNavigate();
  const [switching, setSwitching] = useState(false);
  const otherRole = currentRole === "homeowner" ? "tradesperson" : "homeowner";
  const otherLabel = otherRole === "homeowner" ? "Homeowner" : "Contractor";

  const doSwitch = async () => {
    if (!window.confirm(`Switch to a ${otherLabel} account? You'll keep the same email + password.`)) return;
    setSwitching(true);
    try {
      await api.put("/me/role", { role: otherRole });
      await onSwitched();
      toast.success(`You're now signed in as a ${otherLabel}.`);
      nav(otherRole === "tradesperson" ? "/profile/edit" : "/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not switch role.");
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div>
      <div className="text-sm text-slate-600 mb-3">
        You&apos;re currently signed in as a <span className="font-semibold text-slate-900">{currentRole === "homeowner" ? "Homeowner" : "Contractor"}</span>.
      </div>
      <Button
        type="button"
        data-testid="switch-role-btn"
        onClick={doSwitch}
        disabled={switching}
        variant="outline"
        className="border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white"
      >
        {switching ? "Switching…" : `Switch to ${otherLabel}`}
      </Button>
    </div>
  );
}
